import React, { memo } from "react";
import { animated, config, useSpring } from "@react-spring/web";
import styles from "./PrivateSidemenu.module.scss";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logOut } from "../../../stores/slices/current-user-slice";
import { ApplicationRoutes } from "../../../routes";
import PrivateSidemenuButton, { PrivateSidemenuButtonProps } from "./PrivateSidemenuButton";
import { UserType } from "../../../model/userType";
import { getUserType } from "../../../guards/common/authentification-functions";
import { CloseMobileMenuIcon, DashboardIcon, HistoryIcon, LogOutIcon, OrdersIcon } from "../../../assets/icons";
import { CustomersIcon, PeopleIcon, ProductIcon, SettingsIcon, SolarProviderIcon } from "../../../assets/sidemenu-icons";

type Props = {
	isColapsed: boolean;
	isDisabled: boolean;
	mobileMenuOpen: boolean;
	onMobileMenuClick: () => void;
};

const PrivateSidemenu: React.FC<Props> = memo((props: Props) => {
	const { isColapsed, isDisabled, mobileMenuOpen, onMobileMenuClick } = props;

	const dispatch = useDispatch();
	const navigator = useNavigate();

	const logout = () => {
		dispatch(logOut());
		navigator(ApplicationRoutes.LANDING_PAGE);
	};

	const menuStyles = useSpring({
		from: {
			transform: mobileMenuOpen ? "translateX(-100%)" : "translateX(0%)",
		},
		to: {
			transform: mobileMenuOpen ? "translateX(0%)" : "translateX(-100%)",
		},
		config: config.gentle,
	});

	const providerSidemenuButtons: PrivateSidemenuButtonProps[] = [
		{
			text: "Dashboard",
			icon: <DashboardIcon />,
			colapsed: isColapsed,
			isDisabled: isDisabled,
			route: `${ApplicationRoutes.PROVIDER_BASE}/${ApplicationRoutes.DASHBOARD}`,
		},
		{
			text: "Product",
			icon: <ProductIcon />,
			colapsed: isColapsed,
			isDisabled: isDisabled,
			route: `${ApplicationRoutes.PROVIDER_BASE}/${ApplicationRoutes.PRODUCTS}`,
		},
		{
			text: "Customers",
			icon: <CustomersIcon />,
			colapsed: isColapsed,
			isDisabled: isDisabled,
			route: `${ApplicationRoutes.PROVIDER_BASE}/${ApplicationRoutes.PROVIDER_CUSTOMERS}`,
		},
		{
			text: "Settings",
			icon: <SettingsIcon />,
			colapsed: isColapsed,
			isDisabled: isDisabled,
			subMenuItems: [
				{
					text: "Team Members",
					route: `${ApplicationRoutes.PROVIDER_BASE}/${ApplicationRoutes.PROVIDER_TEAM_MEMBERS}`,
				},
				{
					text: "Settlement Account",
					route: `${ApplicationRoutes.PROVIDER_BASE}`,
					disabled: true,
				},
			],
		},
	];

	const adminSidemenuButtons: PrivateSidemenuButtonProps[] = [
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Dashboard",
			icon: <DashboardIcon />,
			route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.DASHBOARD}`,
		},
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Solar Provider",
			icon: <SolarProviderIcon />,
			route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.SOLAR_PROVIDER}`,
		},
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Customer",
			icon: <PeopleIcon />,
			route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.CUSTOMER_BASE}`,
		},
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Appliances",
			icon: <PeopleIcon />,
			subMenuItems: [
				{ route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.APPLIANCE_CATEGORIES}`, text: "Appliance categories" },
				{ route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.SEARCH_PARAMETER}`, text: "Appliance items" }],
		},
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Loan Summary Page",
			icon: <PeopleIcon />,
			route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.LOAN_SUMMARY}`,
		},
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Loan Product Page",
			icon: <PeopleIcon />,
			route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.LOAN_PRODUCTS}`,
		},
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Loan Request Page",
			icon: <PeopleIcon />,
			route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.LOAN_REQUEST}`,
		},
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Settings",
			icon: <SettingsIcon />,
			subMenuItems: [
				{
					text: "Product Category",
					route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.PRODUCTS_CATEGORY}`,
				},
				{
					text: "Roof Type",
					route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.ROOF_TYPE}`,
				},
				{
					text: "Store Banner",
					route: `${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.ADVERT_BANNER}`,
				},
			],
		},
	];

	const customerSideMenuButtons: PrivateSidemenuButtonProps[] = [
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Dashboard",
			icon: <DashboardIcon />,
			route: `/${ApplicationRoutes.CUSTOMER_BASE}/${ApplicationRoutes.CUSTOMER_DASHBOARD}`,
		},
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Orders",
			icon: <OrdersIcon />,
			route: `/${ApplicationRoutes.CUSTOMER_BASE}/${ApplicationRoutes.CUSTOMER_ORDERS}`,
		},
		{
			colapsed: isColapsed,
			isDisabled: isDisabled,
			text: "Payment History",
			icon: <HistoryIcon />,
			route: `/${ApplicationRoutes.CUSTOMER_BASE}/${ApplicationRoutes.PAYMENTS}`,
		},
	];

	const renderButtons = () => {
		let buttons: JSX.Element[] = [];

		let userType = getUserType();

		let executeWrapper = (func: () => void) => {
			func();
			onMobileMenuClick();
		};

		if (userType === UserType.BPSAdmin) {
			buttons = adminSidemenuButtons.map((button, index) => {
				button.executeWrapper = executeWrapper;

				return <PrivateSidemenuButton key={index} {...button} />;
			});
		} else if (userType === UserType.Provider) {
			buttons = providerSidemenuButtons.map((button, index) => {
				button.executeWrapper = executeWrapper;

				return <PrivateSidemenuButton key={index} {...button} />;
			});
		} else if (userType === UserType.Customer) {
			buttons = customerSideMenuButtons.map((button, index) => {
				button.executeWrapper = executeWrapper;

				return <PrivateSidemenuButton key={index} {...button} />;
			});
		}

		return buttons;
	};

	return (
		<>
			<div className={`${styles.container}`}>
				{renderButtons()}
				<button className={`standardButton ${styles.logoutButton} ${isColapsed ? styles.colapsed : ""}`} onClick={logout}>
					<div className={styles.text}>Logout</div>
					<LogOutIcon />
				</button>
			</div>
			<animated.div style={menuStyles} className={styles.mobileMenuContainer}>
				<div className={styles.innerContainer}>
					<div className={styles.topbar}>
						<img className={styles.logo} src="../../../images/landing-page-header-icon.png" alt="placeholder" />
						<CloseMobileMenuIcon onClick={onMobileMenuClick} style={{ color: "#42577A" }} />
					</div>
					<div className={styles.buttonSection}>{renderButtons()}</div>
					<div className={styles.logoutContainer}>
						<button className={styles.mobileLogout} onClick={logout}>
							<LogOutIcon />
							<span className={styles.text}>Logout</span>
						</button>
					</div>
				</div>
			</animated.div>
		</>
	);
});

export default PrivateSidemenu;
